import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics, faqs } from '@/mocks/comics';
import { siteInfo } from '@/mocks/site';
import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

const colorMap: Record<string, { bg: string; text: string; hover: string; iconBg: string }> = {
  primary: {
    bg: 'bg-primary-50',
    text: 'text-primary-600',
    hover: 'hover:bg-primary-100',
    iconBg: 'bg-primary-100',
  },
  accent: {
    bg: 'bg-accent-50',
    text: 'text-accent-600',
    hover: 'hover:bg-accent-100',
    iconBg: 'bg-accent-100',
  },
  secondary: {
    bg: 'bg-secondary-50',
    text: 'text-secondary-600',
    hover: 'hover:bg-secondary-100',
    iconBg: 'bg-secondary-100',
  },
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const topRated = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const recentComics = [...comics].sort((a, b) => b.id - a.id).slice(0, 8);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '新新漫画',
    url: 'https://xinxinmanhua.com',
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://xinxinmanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '新新漫画',
    url: 'https://xinxinmanhua.com',
    description: siteInfo.description,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.contact.email,
      contactType: 'customer service',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[560px] md:h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20comic%20book%20illustration%20background%20with%20warm%20golden%20orange%20and%20amber%20gradient%20tones%2C%20abstract%20ink%20splash%20effects%2C%20dynamic%20manga%20panel%20composition%2C%20soft%20artistic%20texture%2C%20subtle%20speed%20lines%2C%20vibrant%20yet%20warm%20color%20palette%2C%20no%20text%2C%20simple%20elegant%20atmosphere%2C%20professional%20website%20background&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
            alt="新新漫画 - 韩漫日漫国漫大全"
            title="新新漫画 - 热门漫画类型分类推荐"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 text-balance leading-tight">
              韩漫日漫国漫大全，
              <br className="hidden md:block" />
              热门漫画一次收藏
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              按类型分类，韩漫日漫国漫大全，热门分类一目了然，
              <br className="hidden md:block" />
              找到你想看的漫画类型从这里开始
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/hanman"
                className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
              >
                <i className="ri-compass-3-line mr-2" />
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/15 text-white backdrop-blur-sm font-semibold rounded-full hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/20"
              >
                <i className="ri-information-line mr-2" />
                了解更多
              </Link>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center p-1.5">
              <div className="w-1 h-2.5 bg-white/60 rounded-full animate-bounce" />
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-white border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {siteInfo.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                漫画类型分类
              </h2>
              <p className="text-foreground-500 mt-3 text-sm md:text-base max-w-xl mx-auto">
                韩漫日漫国漫大全，热门分类一目了然，找到你想看的漫画类型从这里开始
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {categories.map((cat) => {
                const c = colorMap[cat.color] || colorMap.primary;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`${c.bg} ${c.hover} rounded-xl p-5 text-center transition-all duration-200 cursor-pointer group`}
                  >
                    <div className={`w-12 h-12 mx-auto rounded-full ${c.iconBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <i className={`${cat.icon} ${c.text} text-xl`} />
                    </div>
                    <h3 className={`font-semibold text-sm ${c.text}`}>{cat.name}</h3>
                    <p className="text-xs text-foreground-500 mt-1.5 leading-relaxed">{cat.description}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Hot Picks Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  热门推荐
                </h2>
                <p className="text-foreground-500 text-sm mt-2">评分最高、最受读者欢迎的漫画精选</p>
              </div>
              <Link
                to="/category/hanman"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5 md:gap-6" data-product-shop>
              {topRated.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Feature Banner */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20orange%20and%20amber%20abstract%20gradient%20background%20with%20soft%20ink%20wash%20texture%2C%20artistic%20Japanese%20sumi-e%20style%20subtle%20elements%2C%20elegant%20and%20warm%20atmosphere%2C%20no%20text%2C%20clean%20minimal%20composition%2C%20professional%20website%20section%20background&width=1600&height=500&seq=cta-bg-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-500/80" />

          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white font-heading mb-4">
              找不到想看的漫画？
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl mx-auto">
              新新漫画涵盖韩漫、日漫、国漫等丰富类型，总有你喜欢的！从热门推荐开始，发现你的下一部心头好。
            </p>
            <Link
              to="/category/guoman"
              className="inline-flex items-center px-8 py-3 bg-white text-primary-600 font-semibold rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-rocket-line mr-2" />
              立即浏览全部漫画
            </Link>
          </div>
        </section>

        {/* Recent Updates */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  最新收录
                </h2>
                <p className="text-foreground-500 text-sm mt-2">持续更新，发现更多精彩漫画</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5 md:gap-6" data-product-shop>
              {recentComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                常见问题
              </h2>
              <p className="text-foreground-500 mt-3 text-sm">关于新新漫画，你想知道的都在这里</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-background-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="font-medium text-foreground-800 text-sm md:text-base pr-4">{faq.question}</span>
                    <i className={`text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-3">
              准备好开始探索了吗？
            </h2>
            <p className="text-foreground-500 text-sm mb-6">
              加入新新漫画，发现更多精彩漫画世界
            </p>
            <Link
              to="/category/hanman"
              className="inline-flex items-center px-8 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line mr-2" />
              开始浏览
            </Link>
          </div>
        </section>

        <Breadcrumb items={[{ label: '首页' }]} />
      </main>

      <Footer />
    </>
  );
}